import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'

import { useCurrentWorkspace } from '../../store/hooks'
import { updateBusinessDetails } from '../../lib/db'
import { WorkspaceType } from '../../lib/db/db.types'
import { setCurrentWorkspace } from '../../store/slices/workspaceSlice'

enum SetupStep {
  LOADING,
  VAT,
  COMPLETE,
}

function Wrapper({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-center min-h-screen p-8">
      <div className="bg-gray-100 p-8 rounded-xl w-3/4 min-w-96 max-w-screen-md">
        {children}
      </div>
    </div>
  )
}

export const GetStarted = ({ businessId }: { businessId: string }) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const [step, setStep] = useState<SetupStep>(SetupStep.LOADING)

  const { myWorkspaces, calendarEvents, repeatingEvents } =
    useCurrentWorkspace()

  const businessWorkspace = myWorkspaces.find(
    (workspace) => workspace.id === businessId,
  )

  const isBusinessSpace =
    businessWorkspace?.spaceType === WorkspaceType.BUSINESS

  const hasBasicData =
    !!businessWorkspace &&
    calendarEvents.length > 0 &&
    repeatingEvents.length > 0

  const business =
    businessWorkspace?.spaceType === WorkspaceType.BUSINESS
      ? businessWorkspace.business
      : undefined

  useEffect(() => {
    if (businessWorkspace) {
      dispatch(setCurrentWorkspace({ id: businessWorkspace.id }))
    }
  }, [businessWorkspace, dispatch])

  useEffect(() => {
    console.log({ hasBasicData, isBusinessSpace })
    if (hasBasicData) {
      if (isBusinessSpace) {
        setStep(SetupStep.VAT)
      } else {
        setStep(SetupStep.COMPLETE)
      }
    }
  }, [hasBasicData, isBusinessSpace])

  const handleVatResponse = async (isVatRegistered: boolean | null) => {
    if (!businessWorkspace) return

    if (typeof isVatRegistered === 'boolean') {
      await updateBusinessDetails({
        businessId: businessWorkspace.id,
        updates: {
          vatSettings: {
            vatRegistered: isVatRegistered,
          },
        },
      })
    }

    setStep(SetupStep.COMPLETE)
  }

  if (step === SetupStep.LOADING || !businessWorkspace) {
    return (
      <Wrapper>
        <div className="text-center">
          <h1 className="text-3xl font-bold mb-4">Setting up your space</h1>
          <p>Please wait while we finish setting everything up...</p>
        </div>
      </Wrapper>
    )
  }

  if (step === SetupStep.VAT) {
    return (
      <Wrapper>
        <div className="text-center">
          <h1 className="text-3xl font-bold mb-4">Setup VAT calendar</h1>
          <p className="mb-2 text-lg">
            Is {business?.business.name ?? 'your business'} registered for VAT?
          </p>
          <p className="mb-8 text-sm">
            We'll use this information to set up your VAT calendar, to help you
            keep track of your VAT deadlines.
          </p>
          <div className="flex flex-col space-y-4 items-center">
            <div className="flex flex-row items-center justify-center space-x-4">
              <button
                onClick={() => handleVatResponse(true)}
                className="p-2 w-32 bg-blue-500 text-white rounded-md hover:bg-blue-600"
              >
                Yes
              </button>
              <button
                onClick={() => handleVatResponse(false)}
                className="p-2 w-32 bg-blue-500 text-white rounded-md hover:bg-blue-600"
              >
                No
              </button>
            </div>
            <div className="flex flex-col items-center gap-1">
              <button
                onClick={() => handleVatResponse(null)}
                className="p-1 text-blue-500 hover:text-blue-600"
              >
                Skip for now
              </button>
              <small className="text-gray-500">You can add this later</small>
            </div>
          </div>
        </div>
      </Wrapper>
    )
  }

  return (
    <Wrapper>
      <div className="text-center">
        <h1 className="text-3xl font-bold mb-4">All set!</h1>
        <p className="mb-6">Your business is ready to go.</p>
        <button
          onClick={() => navigate(`/workspace/${businessWorkspace.id}/`)}
          className="p-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
        >
          Go to Dashboard
        </button>
      </div>
    </Wrapper>
  )
}
